import { useMemo } from "react"

import { classAtoms, classSessionAtoms, tryoutAtoms } from "#atoms"
import { ClassSessionConstructor } from "#customTypes/composedTypes/classSessionTypes"
import { ClassConstructor } from "#customTypes/composedTypes/classTypes"
import { DOC_TYPES } from "#customTypes/composedTypes/firebaseTypes"
import {
  TryoutConstructor,
  TryoutProgressFormData,
} from "#customTypes/composedTypes/tryoutTypes"
import { Uuid } from "#customTypes/globalTypes"
import { useGetOneStudent } from "#hooks/jotai/useGetStudents"
import { useOneDoc } from "#hooks/jotai/useSubscription"
import { getSessionInfoFromId } from "#utils/classHelpers"
import { getInitialTryoutProgressFormData } from "#utils/tryoutHelpers"

const useTryoutProgressFormData = ({
  tryoutId,
}: {
  tryoutId: Uuid | undefined
}) => {
  const tryout = useOneDoc<TryoutConstructor>(
    tryoutAtoms,
    DOC_TYPES.TRYOUTS,
    tryoutId
  )

  const student = useGetOneStudent(tryout?.studentId)
  const [classId] = getSessionInfoFromId(tryout?.sessionId ?? "")

  const tryoutClass = useOneDoc<ClassConstructor>(
    classAtoms,
    DOC_TYPES.CLASSES,
    classId
  )

  const tryoutSession = useOneDoc<ClassSessionConstructor>(
    classSessionAtoms,
    DOC_TYPES.CLASS_SESSIONS,
    tryout?.sessionId
  )

  // Ready when we have the minimum data needed for form initialization and handlers
  const isReady = !!(tryout && tryoutClass && tryoutSession)

  const initialFormState: TryoutProgressFormData | null = useMemo(() => {
    if (!tryout || !student) return null
    return getInitialTryoutProgressFormData(tryout, student)
  }, [tryout, student])

  return {
    tryout,
    student,
    tryoutClass,
    tryoutSession,
    initialFormState,
    isReady,
  }
}

export default useTryoutProgressFormData
